import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, CalendarOff, Clock, FilePen, Home, LogIn, Search, UserX, Users } from 'lucide-react';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Input } from '../../../components/Input';
import { StatCard } from '../../../components/StatCard';
import { EmptyState } from '../../../components/EmptyState';
import { useAttendanceStore, type AdminDayRow } from '../../../store/attendanceStore';
import { AdjustmentDialog } from './AdjustmentDialog';
import { DayDetailSheet } from './DayDetailSheet';
import { DayStatusBadge, fmtMinutes, fmtTime, localDateString } from './attendanceUi';

const fieldClass = 'h-9 rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30';

type Filter = 'ALL' | 'PRESENT' | 'LATE' | 'ABSENT' | 'NOT_CHECKED_IN' | 'ON_LEAVE' | 'WFH' | 'MISSING_CHECKOUT' | 'EXCEPTION' | 'PENDING';

const matches = (r: AdminDayRow, f: Filter) => {
    switch (f) {
        case 'ALL': return true;
        case 'PRESENT': return ['PRESENT', 'IN_PROGRESS', 'HALF_DAY', 'MISSING_CHECKOUT'].includes(r.status);
        case 'LATE': return r.isLate;
        case 'WFH': return r.workMode === 'WFH' || r.workMode === 'MIXED';
        case 'MISSING_CHECKOUT': return r.hasMissingCheckout;
        case 'EXCEPTION': return r.hasLocationException;
        case 'PENDING': return r.pendingAdjustments > 0;
        default: return r.status === f;
    }
};

/** Managers (their team) and HR (everyone): who is in, who isn't, and why. */
export function TeamPanel({ canCorrect }: { canCorrect: boolean }) {
    const { adminSummary, adminRows, adminLoading, fetchAdminSummary, fetchAdminDaily } = useAttendanceStore();
    const [date, setDate] = useState(localDateString());
    const [filter, setFilter] = useState<Filter>('ALL');
    const [search, setSearch] = useState('');
    const [detail, setDetail] = useState<{ employeeId: string; date: string } | null>(null);
    const [correcting, setCorrecting] = useState<{ id: string; name: string | null } | null>(null);
    const isToday = date === localDateString();

    const load = () => Promise.all([fetchAdminSummary(date), fetchAdminDaily({ date })]).catch((e) => toast.error(e.message));
    useEffect(() => { load(); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return adminRows.filter((r) => matches(r, filter)
            && (!q || `${r.employeeName} ${r.employeeCode} ${r.department}`.toLowerCase().includes(q)));
    }, [adminRows, filter, search]);

    const k = adminSummary;
    const tile = (f: Filter, label: string, value: number | undefined, icon: React.ReactNode, hint?: string) => (
        <StatCard key={f} label={label} value={value ?? '—'} icon={icon} hint={hint}
            className={filter === f ? 'ring-2 ring-primary/40' : ''} onClick={() => setFilter(filter === f ? 'ALL' : f)} />
    );

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <input className={fieldClass} type="date" value={date} max={localDateString()} onChange={(e) => setDate(e.target.value)} aria-label="Date" />
                    {!isToday && <Button size="sm" variant="ghost" onClick={() => setDate(localDateString())}>Today</Button>}
                </div>
                <div className="w-full sm:w-72">
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, code or department"
                        leftIcon={<Search className="size-4 text-muted-foreground" />} />
                </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {tile('ALL', 'Employees', k?.totalEmployees, <Users className="size-4" />)}
                {tile('PRESENT', 'Present', k?.present, <LogIn className="size-4" />, isToday ? `${k?.checkedInNow ?? 0} checked in now` : undefined)}
                {tile('LATE', 'Late', k?.late, <Clock className="size-4" />)}
                {isToday
                    ? tile('NOT_CHECKED_IN', 'Not checked in', k?.notCheckedIn, <UserX className="size-4" />)
                    : tile('ABSENT', 'Absent', k?.absent, <UserX className="size-4" />)}
                {tile('ON_LEAVE', 'On leave', k?.onLeave, <CalendarOff className="size-4" />)}
                {tile('WFH', 'Working from home', k?.wfh, <Home className="size-4" />)}
                {tile('MISSING_CHECKOUT', 'Missing check-out', k?.missingCheckout, <AlertTriangle className="size-4" />,
                    k?.locationExceptions ? `${k.locationExceptions} location exception(s)` : undefined)}
                {tile('PENDING', 'Pending adjustments', k?.pendingAdjustments, <FilePen className="size-4" />)}
            </div>

            <Card>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                    <h2 className="font-display text-card-title text-foreground">
                        {filter === 'ALL' ? 'All employees' : 'Filtered'} <span className="text-sm font-normal text-muted-foreground">{rows.length}</span>
                    </h2>
                    {filter !== 'ALL' && <Button size="sm" variant="ghost" onClick={() => setFilter('ALL')}>Clear filter</Button>}
                </div>
                {rows.length === 0 ? (
                    <EmptyState className="m-4 border-0" title={adminLoading ? 'Loading…' : 'Nobody matches'} description="Try another date or filter." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                                <tr>
                                    <th className="px-4 py-2 font-semibold">Employee</th>
                                    <th className="px-4 py-2 font-semibold">Status</th>
                                    <th className="px-4 py-2 font-semibold">First in</th>
                                    <th className="px-4 py-2 font-semibold">Last out</th>
                                    <th className="px-4 py-2 text-right font-semibold">Worked</th>
                                    <th className="px-4 py-2 text-right font-semibold">Sessions</th>
                                    <th className="px-4 py-2 font-semibold">Location</th>
                                    <th className="px-4 py-2" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rows.map((r) => (
                                    <tr key={r.employeeId} className="cursor-pointer hover:bg-secondary/30" onClick={() => setDetail({ employeeId: r.employeeId, date })}>
                                        <td className="px-4 py-2">
                                            <p className="font-medium text-foreground">{r.employeeName || 'Unnamed'}</p>
                                            <p className="text-xs text-muted-foreground">{[r.employeeCode, r.designation || r.department].filter(Boolean).join(' · ')}</p>
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="flex flex-wrap gap-1">
                                                <DayStatusBadge status={r.status} />
                                                {r.isLate && <Badge variant="warning">Late {fmtMinutes(r.lateMinutes)}</Badge>}
                                                {r.hasLocationException && <Badge variant="warning">Location</Badge>}
                                                {r.pendingAdjustments > 0 && <Badge variant="info">Adjustment pending</Badge>}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-2 tabular-nums text-muted-foreground">{fmtTime(r.firstCheckIn)}</td>
                                        <td className="whitespace-nowrap px-4 py-2 tabular-nums text-muted-foreground">{r.checkedInNow && isToday ? 'Checked in' : fmtTime(r.lastCheckOut)}</td>
                                        <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-foreground">{r.sessions ? fmtMinutes(r.workedMinutes) : '—'}</td>
                                        <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-muted-foreground">{r.sessions || '—'}</td>
                                        <td className="px-4 py-2 text-muted-foreground">
                                            {r.locations
                                                ? r.locations
                                                : r.workMode === 'WFH'
                                                    ? 'Home'
                                                    : (r.sessions > 0 || r.checkedInNow)
                                                        ? <span className="italic text-muted-foreground/70">Location not shared</span>
                                                        : '—'}
                                        </td>
                                        <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                                            {canCorrect && (
                                                <Button size="sm" variant="ghost" onClick={() => setCorrecting({ id: r.employeeId, name: r.employeeName })}>Correct</Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <DayDetailSheet target={detail} onClose={() => setDetail(null)} />
            <AdjustmentDialog open={!!correcting} onOpenChange={(o) => !o && setCorrecting(null)} mode="admin"
                employee={correcting || undefined} preset={{ date, type: 'MISSED_BOTH' }} onDone={load} />
        </div>
    );
}
